/*
 * Issue #295, AC-11 to AC-14: the Save Results phone field's input handling.
 *
 * DELIBERATE DUPLICATE of Services/Calculator/CalculatorPhoneNumber.cs, which is the authoritative
 * copy of these rules. Same pattern as the seller-concession client math
 * (.claude/rules/lock-products-seller-concession-units.md): the C# seam is what a hand-crafted POST
 * is measured against and is the only copy with test coverage -- there is no JavaScript test runner
 * in this repo, and adding one is its own piece of work
 * (.claude/rules/lock-products-controller-testability.md). This file exists for the typing
 * experience. If you change a rule here, change it there, and let the C# tests be the ones that
 * prove it.
 *
 * Why not jquery.inputmask, which every other phone field in the app uses (partner-appraiser.js and
 * six others, all "(999) 999-9999" with clearIncomplete: true):
 *   - AC-12 wants a separator only once the digit after it exists -- 3 digits render "(973)" and 4
 *     render "(973)-5", with never a trailing "(", ")" or "-" waiting for input. inputmask emits its
 *     skeleton and the opening parenthesis immediately.
 *   - The format is "(973)-534-7079", hyphen straight after the closing parenthesis, not the site's
 *     "(999) 999-9999". The hyphen there is Phil's specification, not a typo.
 *   - clearIncomplete wipes a partial number on blur; AC-14 requires 1-9 digits to raise an inline
 *     error and block the submission.
 */

/** A complete US number. */
export const REQUIRED_DIGITS = 10;

/**
 * Reduces a value to the ASCII digits it contains.
 *
 * Deliberately /[^0-9]/ and not \D with a Unicode flag, matching the C# seam's reason for avoiding
 * char.IsDigit: AC-11 names the digits 0-9, and the full-width and Arabic-Indic digit forms a paste
 * from a non-Latin page carries would otherwise survive and produce a number that cannot be dialled.
 */
export function extractDigits(value) {
    return String(value == null ? '' : value).replace(/[^0-9]/g, '');
}

/**
 * Drops a leading US country code: exactly 11 digits beginning with 1 become the trailing 10
 * (AC-11). Scoped to exactly 11 so a 10-digit number starting with 1 is left alone.
 */
export function stripCountryCode(digits) {
    return digits.length === REQUIRED_DIGITS + 1 && digits.charAt(0) === '1'
        ? digits.slice(1)
        : digits;
}

/**
 * The input path: digits only, country code dropped, then truncated to the cap. AC-13 requires an
 * overflowing paste to fill to 10 and discard the surplus rather than being rejected wholesale.
 */
export function normalize(value) {
    return stripCountryCode(extractDigits(value)).slice(0, REQUIRED_DIGITS);
}

/**
 * The AC-12 progressive display. A separator is emitted only when the digit that follows it exists,
 * so the result always ends in a digit: 3 digits are "(973)", 6 are "(973)-534".
 */
export function formatDigits(digits) {
    if (!digits) return '';
    if (digits.length <= 2) return digits;
    if (digits.length === 3) return '(' + digits + ')';
    if (digits.length <= 6) return '(' + digits.slice(0, 3) + ')-' + digits.slice(3);

    return '(' + digits.slice(0, 3) + ')-' + digits.slice(3, 6) + '-' + digits.slice(6);
}

/** Reduces arbitrary input and formats it. Typing "(973) 534-7079" and "9735347079" agree. */
export function format(value) {
    return formatDigits(normalize(value));
}

/** True when the field holds a submittable value: blank, or exactly ten digits (AC-14). */
export function isAcceptable(value) {
    const digits = stripCountryCode(extractDigits(value));

    return digits.length === 0 || digits.length === REQUIRED_DIGITS;
}

/** How many digits sit before `caret` in `value` -- the AC-12 cursor unit. */
function digitsBefore(value, caret) {
    return extractDigits(String(value).slice(0, caret)).length;
}

/**
 * The caret offset immediately after the `count`-th digit of `formatted`.
 *
 * AC-12: an edit in the middle of the value "leaves the cursor immediately after the digit just
 * inserted or deleted, counting digits only and ignoring the punctuation the field supplies". So the
 * caret is tracked as a digit index across the reformat, not as a character offset -- a character
 * offset shifts whenever the reformat adds or removes a parenthesis.
 */
function caretAfterDigit(formatted, count) {
    if (count <= 0) return 0;

    let seen = 0;

    for (let i = 0; i < formatted.length; i += 1) {
        if (formatted.charAt(i) >= '0' && formatted.charAt(i) <= '9') {
            seen += 1;

            if (seen === count) return i + 1;
        }
    }

    return formatted.length;
}

/**
 * Attaches the digits-only, auto-formatting behaviour to one input.
 *
 * Two listeners, for two different reasons:
 *
 *  - `beforeinput` cancels a non-digit *keystroke* before it lands, which is what AC-11's "no flash
 *    of the invalid character" requires. Only insertText is inspected, so Backspace, Delete, Tab,
 *    the arrows, Home/End, select-all and undo are untouched -- they never reach this branch.
 *  - `input` reformats whatever ended up in the field. This is the path for paste, drag-and-drop,
 *    autofill, and any browser that does not fire a cancelable beforeinput. AC-11 explicitly allows
 *    a pasted value to be "stripped to its digits and reformatted", so a reformat here rather than a
 *    rejection is the specified behaviour.
 *
 * @param {HTMLInputElement} input the phone input
 */
export function attachPhoneFormatting(input) {
    if (!input) return;

    input.addEventListener('beforeinput', (event) => {
        if (event.inputType === 'insertText') {
            // null data is an IME composition start; let `input` normalise that one.
            if (event.data != null && /[^0-9]/.test(event.data)) {
                event.preventDefault();
            }
            return;
        }

        // AC-12: "Backspace and Delete remove one digit at a time... the visitor never has to press
        // Backspace twice to get past a parenthesis or hyphen the field inserted."
        //
        // Without this, a caret placed by click or arrow key immediately after a separator deletes
        // the separator, leaves the digit count unchanged, and the reformat in the `input` handler
        // puts the identical string back -- so the press appears to do nothing and a second one is
        // needed. Typing forward never hits it (the caret always lands after a digit), which is why
        // it only shows up on a manual caret placement. Here we take over the delete and remove the
        // nearest DIGIT in the direction of travel.
        const back = event.inputType === 'deleteContentBackward';
        const fwd = event.inputType === 'deleteContentForward';
        if (!back && !fwd) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;

        // A selection deletes exactly what is selected; the reformat handles the remainder.
        if (start !== end) return;

        const digits = extractDigits(input.value);
        const digitsBeforeCaret = extractDigits(input.value.slice(0, start)).length;
        const target = back ? digitsBeforeCaret - 1 : digitsBeforeCaret;

        if (target < 0 || target >= digits.length) return;

        event.preventDefault();

        const remaining = digits.slice(0, target) + digits.slice(target + 1);
        input.value = formatDigits(normalize(remaining));

        const caret = caretAfterDigit(input.value, target);
        try {
            input.setSelectionRange(caret, caret);
        } catch (e) {
            /* selection unsupported; the value is already correct */
        }

        // The listener below is bound to `input`, which a cancelled beforeinput never fires, so the
        // error-clearing and any other input-driven behaviour has to be triggered explicitly.
        input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    input.addEventListener('input', () => {
        const caret = typeof input.selectionStart === 'number' ? input.selectionStart : null;
        const targetDigit = caret === null ? null : digitsBefore(input.value, caret);
        const formatted = format(input.value);

        if (formatted === input.value) return;

        input.value = formatted;

        if (targetDigit !== null) {
            // Clamp: an overflowing paste can leave fewer digits than sat before the caret.
            const digitCount = extractDigits(formatted).length;
            const position = caretAfterDigit(formatted, Math.min(targetDigit, digitCount));

            try {
                input.setSelectionRange(position, position);
            } catch (e) {
                // setSelectionRange throws on an input type that does not support selection in some
                // browsers. The value is already correct; the caret is cosmetic.
            }
        }
    });
}
